import { z } from "zod";
import { text } from "./common";

/* AI assist (Phase 7). AI writes drafts; people accept, edit or reject them. */

export const AI_FEATURES = ["documentation", "summary", "communication", "receptionist", "analytics"] as const;
export type AiFeature = (typeof AI_FEATURES)[number];
export const AI_FEATURE_LABELS: Record<AiFeature, string> = {
  documentation: "Visit note drafts from the dentist's shorthand",
  summary: "Patient summaries before a visit",
  communication: "Message drafts for patients",
  receptionist: "Booking help at the front desk",
  analytics: "Plain-language explanations of reports",
};

export interface AiStatus {
  /** The server has an AI provider configured. */
  available: boolean;
  /** The clinic's plan includes AI. */
  entitled: boolean;
  /** The clinic switched AI on. */
  enabled: boolean;
  features: AiFeature[];
  model: string | null;
  monthToDate: { requests: number; costMicros: number };
  monthlyCapMicros: number | null;
  acknowledgedBy: string | null;
  acknowledgedAt: string | null;
  version: number | null;
  /** Why AI cannot be used right now (empty when it can). */
  problems: string[];
}

export const aiSettingsInput = z
  .object({
    enabled: z.boolean(),
    features: z.array(z.enum(AI_FEATURES)).max(AI_FEATURES.length),
    /** USD per month; null = no cap. */
    monthlyCapUsd: z.number().positive().max(100_000).nullish().transform((v) => v ?? null),
    /** Required to switch on: the person accepts the data notice. */
    acknowledgeDataNotice: z.boolean().default(false),
    version: z.number().int().positive().nullish().transform((v) => v ?? null),
  })
  .refine((s) => new Set(s.features).size === s.features.length, { path: ["features"], message: "Duplicate feature" });

/* --------------------------------------------------------------------------------- outputs */

// Output schemas stay plain (structured outputs accept a subset of JSON Schema); lengths are checked after parsing.
const clause = z.string();

/** Visit note draft: maps 1:1 to the clinical note fields. */
export const noteDraftSchema = z.object({
  chiefComplaint: clause,
  history: clause,
  examination: clause,
  assessment: clause,
  plan: clause,
  /** Things the shorthand did not make clear, for the dentist to check. */
  uncertainties: z.array(z.string()),
});
export type NoteDraft = z.infer<typeof noteDraftSchema>;

export const summaryDraftSchema = z.object({
  summary: z.string(),
  /** Safety-relevant points: allergies, critical conditions, medications that matter for treatment. */
  alerts: z.array(z.string()),
  openTreatment: z.array(z.string()),
});
export type SummaryDraft = z.infer<typeof summaryDraftSchema>;

export const messageDraftSchema = z.object({ text: z.string() });
export type MessageDraft = z.infer<typeof messageDraftSchema>;

/* --------------------------------------------------------------------------------- inputs */

export const noteDraftInput = z.object({ shorthand: text(4000) });
export const messageDraftInput = z.object({
  channel: z.enum(["whatsapp", "email", "sms"]).default("whatsapp"),
  /** What the message is for, in staff words ("remind about unpaid bill", "post-extraction care"). */
  purpose: text(500),
  tone: z.enum(["warm", "formal", "brief"]).default("warm"),
});

export const reviewDraftInput = z.object({
  version: z.number().int().positive(),
  decision: z.enum(["accept", "reject"]),
  /** The reviewer's corrected version (same shape as the draft). Accepting with edits marks the draft "edited". */
  edited: z.record(z.string(), z.unknown()).nullish().transform((v) => v ?? null),
  note: z.string().trim().max(500).nullish().transform((v) => v || null),
});

export const AI_DRAFT_STATUSES = ["draft", "accepted", "edited", "rejected"] as const;

export interface AiDraft {
  id: string;
  feature: AiFeature;
  targetEntityType: string;
  targetEntityId: string;
  patientId: string | null;
  content: Record<string, unknown>;
  editedContent: Record<string, unknown> | null;
  status: (typeof AI_DRAFT_STATUSES)[number];
  model: string;
  createdBy: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  resultEntityType: string | null;
  resultEntityId: string | null;
  createdAt: string;
  version: number;
}

/* ------------------------------------------------------------------ receptionist + analytics (7b) */

export const TIMES_OF_DAY = ["morning", "afternoon", "evening", "any"] as const;

/** What the front-desk request asks for. AI only interprets; the slot engine finds the times. */
export const bookingIntentSchema = z.object({
  /** One of the service ids given in the prompt, or null if unclear. */
  serviceId: z.string().nullable(),
  /** One of the doctor ids given in the prompt, or null for any doctor. */
  doctorId: z.string().nullable(),
  earliestDate: z.string(),
  latestDate: z.string(),
  timeOfDay: z.enum(TIMES_OF_DAY),
  /** Anything the staff member should check (unclear treatment, conflicting wishes). */
  notes: z.string(),
});
export type BookingIntent = z.infer<typeof bookingIntentSchema>;

export const bookingAssistInput = z.object({
  /** The patient's wish in staff words, e.g. "cleaning next Tuesday evening with Dr Meera". */
  request: text(1000),
  branchId: z.uuid().nullish().transform((v) => v ?? null),
});

export interface BookingOption {
  startsAt: string;
  doctorId: string;
  doctorName: string;
  serviceId: string | null;
  serviceName: string | null;
}

export interface BookingAssistResult {
  draft: AiDraft;
  intent: BookingIntent;
  branchId: string;
  options: BookingOption[];
}

export const reportExplanationSchema = z.object({
  headline: z.string(),
  highlights: z.array(z.string()),
  /** Limits of the numbers (small samples, missing data) so nobody over-reads them. */
  cautions: z.array(z.string()),
});
export type ReportExplanation = z.infer<typeof reportExplanationSchema>;

export const explainReportInput = z.object({
  key: z.string().regex(/^[a-z_]+$/),
  from: z.iso.date(),
  to: z.iso.date(),
  branchId: z.uuid().nullish().transform((v) => v ?? null),
});
